import * as React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Copy } from "lucide-react";
import { cn } from "../lib/cn";
import { tableToTsv } from "../lib/tableToTsv";
import { SkeletonParagraph } from "../primitives/Skeleton";

/**
 * Markdown — sealed renderer for chat-aligned markdown content.
 *
 * Renders user-supplied markdown with the defaults that AISquare's chat
 * surfaces share:
 *
 * - GFM (GitHub-flavored markdown — tables, strikethrough, task lists, autolinks).
 * - Soft line breaks (single newline → `<br>`, via `remark-breaks`) so chat
 *   lines render visually like the user typed them.
 * - HTML sanitization via `rehype-sanitize` with the schema extended to allow
 *   `language-*` className on `<code>` (so syntax highlighting survives the
 *   sanitize pass).
 * - Fenced code blocks render via `react-syntax-highlighter` Prism + `oneDark`.
 *   Blocks WITH a language label show a header bar (language + Copy / Copied
 *   button); blocks WITHOUT a language show a floating copy icon (top-right,
 *   reveal on hover).
 * - Inline code styled as `bg-muted` token.
 * - Tables wrapped in `overflow-x-auto` so they scroll on narrow viewports
 *   instead of overflowing the chat bubble.
 * - Anchors open in a new tab (`target="_blank" rel="noopener noreferrer"`).
 * - Empty / whitespace-only `content` returns `null` (no empty wrapper div).
 *
 * Composition seam: pass a `components` prop with element overrides to
 * intercept specific element renderers (most commonly `a` for app-specific
 * routing or `code` for a custom highlighter). Your overrides shallow-merge
 * with the defaults — keys you provide replace, keys you omit stay default.
 *
 *   <Markdown content={text} />
 *
 *   <Markdown
 *     content={text}
 *     components={{
 *       a: ({ href, children, ...rest }) => {
 *         if (href?.startsWith("#resource:")) return <ResourceLink ... />;
 *         return <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
 *       },
 *     }}
 *   />
 *
 * Streaming: pass `streaming` to render an inline-block blinking caret as
 * the last child of the prose container. The caret pulses while live tokens
 * are still arriving and disappears as soon as you flip the prop to `false`.
 * Empty / whitespace-only content still returns `null` even with `streaming`
 * — surface a `<TypingIndicator>` for the pre-first-token gap rather than an
 * empty wrapper. Re-renders during streaming are cheap thanks to
 * `React.memo` + the prose container's stable shape.
 *
 * SSR: every renderer dependency is SSR-safe. The copy button's
 * `navigator.clipboard.writeText` runs only inside `onClick`, never at render.
 *
 * Voice: the only string this primitive owns is the copy-button label
 * (`"Copy"` / `"Copied!"`). All other text comes from the consumer's content.
 */
export interface MarkdownProps {
  /**
   * Markdown source. Empty / whitespace-only renders nothing (returns null).
   * Consumers preprocess as needed before passing — this primitive does not
   * strip arbitrary blocks (e.g. `<suggestions>`), decode escapes, or
   * otherwise transform input.
   */
  content: string;

  /**
   * Extra classes appended (via `cn`) to the prose container. Use for
   * layout-level overrides (text size, margins). For per-element rendering,
   * pass `components` instead.
   */
  className?: string;

  /**
   * Per-element renderer overrides, shallow-merged with the primitive's
   * defaults. Keys you provide replace the default renderer; keys you omit
   * stay default.
   */
  components?: Partial<Components>;

  /**
   * Render an inline-block blinking caret as the last child of the prose
   * container, signalling that more tokens are arriving. Default `false`.
   * `prefers-reduced-motion` collapses the pulse to a steady block.
   *
   * Empty / whitespace-only `content` still returns `null` even when
   * `streaming` is true — render a `<TypingIndicator>` for that pre-first-
   * token state rather than an empty wrapper.
   */
  streaming?: boolean;

  /**
   * The source has not arrived yet — renders placeholder prose lines instead.
   *
   * This is the state BEFORE `streaming`, and the three answers are distinct:
   * `loading` is "the request is out, no tokens yet"; `streaming` is "tokens
   * are arriving, here is a caret"; and empty `content` with neither flag is
   * "there is no message", which returns `null`.
   *
   * It outranks the whitespace guard below, which would otherwise answer an
   * un-fetched message with `null` and collapse the row to zero height.
   * `streaming` is ignored while this is true — a caret belongs at the end of
   * text that exists.
   */
  loading?: boolean;
  /** Placeholder line count while `loading`. Default 3. */
  loadingLines?: number;
}

/**
 * Rehype-sanitize schema extension that allows `language-*` className on
 * `<code>` elements. Without this, the default schema strips the language
 * marker before `react-syntax-highlighter` can read it, and every fenced
 * block renders as plain text.
 *
 * Exported so consumers extending the renderer with their own rehype
 * plugins can compose against the same baseline.
 */
export const markdownSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...((defaultSchema.attributes?.code as Array<unknown>) ?? []),
      ["className", /^language-/],
    ],
  },
};

const REMARK_PLUGINS = [remarkGfm, remarkBreaks];
// Tuple-of-plugin-with-options needs the explicit cast — `react-markdown`'s
// `PluggableList` accepts both `Plugin` and `[Plugin, ...args]` shapes, but
// inferred-tuple narrows too tight without the assertion.
const REHYPE_PLUGINS: React.ComponentProps<typeof ReactMarkdown>["rehypePlugins"] = [
  [rehypeSanitize, markdownSanitizeSchema],
];

interface CodeBlockProps {
  language: string | undefined;
  code: string;
}

const CodeBlock = React.memo(function CodeBlock({ language, code }: CodeBlockProps) {
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = React.useCallback(() => {
    // Feature-check — older Safari (pre-13.1) lacks `navigator.clipboard`.
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(code).catch(() => {
      // Permission denied or focus issue. Silent no-op rather than crashing
      // the render; the user can still select + copy by hand.
    });
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  }, [code]);

  const ariaLabel = copied ? "Copied to clipboard" : "Copy code";

  return (
    <div className="relative group my-3 rounded-2xl overflow-hidden border border-border/70 not-prose">
      {language ? (
        <div className="flex items-center justify-between px-4 sm:px-5 py-2.5 bg-card text-xs text-muted-foreground border-b border-border/70">
          <span>{language}</span>
          <button
            type="button"
            onClick={handleCopy}
            aria-label={ariaLabel}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3" aria-hidden />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" aria-hidden />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleCopy}
          aria-label={ariaLabel}
          className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-1 rounded bg-muted text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5" aria-hidden />
          ) : (
            <Copy className="w-3.5 h-3.5" aria-hidden />
          )}
        </button>
      )}
      <SyntaxHighlighter
        style={oneDark}
        language={language || "text"}
        PreTag="div"
        wrapLongLines
        customStyle={{
          margin: 0,
          // The wrapper clips the rounded corners (overflow-hidden), so the
          // canvas fills flush and code never collides with a corner. Padding
          // matches the standalone CodeBlock for a consistent code surface.
          borderRadius: 0,
          padding: "1.25rem",
          fontSize: "0.85rem",
          // Wrap long unbroken tokens (URLs, identifiers) instead of
          // forcing the parent container to scroll horizontally. The
          // chat bubble caps at 85% of the panel width, so anything
          // longer than that without these rules pushes the bubble out
          // and the whole conversation scrolls sideways.
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
        codeTagProps={{
          style: {
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          },
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
});
CodeBlock.displayName = "CodeBlock";

/* MarkdownTable — the platform table treatment (PQA#2641, consolidated from
 * the unified chat one-off): a hairline header underline + hairline row
 * dividers via the th/td defaults below, and a hover-revealed "copy table"
 * button in the same idiom as the CodeBlock copy (incl. the unmount timer
 * cleanup the one-off missed). Copies the RENDERED rows as TSV (tableToTsv),
 * so it pastes into a spreadsheet regardless of the markdown source shape.
 * The overflow-x-auto scroller is preserved: consumer overrides REPLACE this
 * renderer per element, and losing the scroller regresses narrow viewports
 * (test-pinned below). */
const MarkdownTable = React.memo(function MarkdownTable({
  children,
  ...props
}: React.ComponentPropsWithoutRef<"table">) {
  const tableRef = React.useRef<HTMLTableElement>(null);
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = React.useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(tableToTsv(table)).catch(() => {
      // Permission denied or focus issue — silent no-op, the user can still
      // select + copy by hand (the CodeBlock idiom's contract).
    });
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  }, []);

  return (
    <div className="group relative overflow-x-auto my-2">
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Copied to clipboard" : "Copy table"}
        className="absolute top-1 right-1 z-10 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity px-1.5 py-0.5 text-[11px] rounded border border-border bg-card/90 text-muted-foreground backdrop-blur-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      >
        {copied ? (
          <>
            <Check className="w-3 h-3" aria-hidden />
            Copied!
          </>
        ) : (
          <>
            <Copy className="w-3 h-3" aria-hidden />
            Copy
          </>
        )}
      </button>
      <table ref={tableRef} className="min-w-full border-collapse text-[13px]" {...props}>
        {children}
      </table>
    </div>
  );
});
MarkdownTable.displayName = "MarkdownTable";

const defaultComponents: Components = {
  code({ className, children, ...props }) {
    const childrenStr = String(children);
    const match = /language-(\w+)/.exec(className ?? "");
    // Distinguish inline vs fenced — react-markdown v10 dropped the `inline`
    // prop, so we infer instead. Three independent signals classify a block;
    // any one of them flips us to the CodeBlock renderer:
    //   1. A `language-*` className from the fence's info string.
    //   2. A trailing newline — react-markdown preserves the `\n` that
    //      CommonMark fenced blocks always carry; inline spans don't.
    //   3. An embedded newline — multi-line content can't be inline.
    // Single-line fenced blocks without a language (e.g. ```\nfoo\n```) hit
    // signal #2 alone, which is why naive `codeString.includes("\n")`
    // mis-classifies them as inline.
    const isFenced = !!match || childrenStr.endsWith("\n") || childrenStr.includes("\n");
    if (!isFenced) {
      return (
        <code
          className="px-1.5 py-0.5 rounded bg-muted text-[0.95em] font-mono break-words [overflow-wrap:anywhere]"
          {...props}
        >
          {children}
        </code>
      );
    }
    const code = childrenStr.replace(/\n$/, "");
    return <CodeBlock language={match?.[1]} code={code} />;
  },
  pre({ children }) {
    // The `code` renderer above already wraps fenced blocks in their own
    // container. Letting the default `<pre>` wrap them too would double-pad
    // and break the rounded corners.
    return <>{children}</>;
  },
  a({ href, children, ...props }) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all [overflow-wrap:anywhere]"
        {...props}
      >
        {children}
      </a>
    );
  },
  table({ children, ...props }) {
    return <MarkdownTable {...props}>{children}</MarkdownTable>;
  },
  /* The PQA#2641 separator treatment: a stronger hairline under the header
     row, lighter hairlines between body rows (the last row's bottom rule
     doubles as the table's closing line). Token-driven so light + dark both
     resolve; consumer overrides replace these per element as before. */
  th({ children, ...props }) {
    return (
      <th
        className="border-b border-border px-3 py-1.5 text-left text-[12px] font-semibold text-foreground"
        {...props}
      >
        {children}
      </th>
    );
  },
  td({ children, ...props }) {
    return (
      <td
        className="border-b border-border/60 px-3 py-1.5 align-top text-muted-foreground"
        {...props}
      >
        {children}
      </td>
    );
  },
};

const PROSE_CLASSES = [
  // Base prose + dark + override `max-w-none` so we don't constrain the chat bubble.
  "prose prose-base dark:prose-invert max-w-none",
  // Tighter chat-friendly spacing — markdown inside a bubble shouldn't
  // dominate the bubble vertically.
  "prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5",
  // Marker gutter wide enough for a TWO-DIGIT ordered marker. Typography ships
  // `1.625em`, and list markers are `list-style-position: outside`, i.e. the
  // browser RIGHT-aligns them against the item's content edge so the periods
  // line up (the `9` sits over the `0` of `10` — the standard we want to keep).
  // Right-alignment means the column's real width is set by the WIDEST marker,
  // and `1.625em` only fits one digit: measured in the context-window panel at
  // 13.5px, the gutter is 21.94px while a right-aligned `10.` plus its gap to
  // the text needs ~26px, so `10.` rendered 1.3px OUTSIDE the container's left
  // text margin — poking into the column every heading and paragraph aligns to
  // (unified #781). `2em` (the value GitHub reserves for the same job) clears
  // it with 3.4px to spare and costs ~5px of extra text indent. Markers of
  // three digits or more still overhang; a prose list reaching 100 items is
  // rare enough not to be worth indenting every other list for.
  "prose-ul:ps-[2em] prose-ol:ps-[2em]",
  // Pre / code: defer to the CodeBlock renderer for visual treatment, so the
  // prose default `<pre>` background doesn't fight the SyntaxHighlighter theme.
  "prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent",
  // Drop the leading top-margin on the first child so the prose container
  // hugs whatever wraps it (chat bubble / card / panel).
  "[&>*:first-child]:mt-0",
  // Wrap long unbroken tokens (URLs, identifiers, monospaced names)
  // inside paragraphs / list items instead of expanding the parent
  // container horizontally. Without this, a single long string in the
  // body of a chat message forces the whole bubble — and the chat
  // panel around it — to scroll sideways.
  "break-words [overflow-wrap:anywhere]",
];

const MarkdownInner = React.forwardRef<HTMLDivElement, MarkdownProps>(function Markdown(
  { content, className, components, streaming = false, loading = false, loadingLines = 3 },
  ref,
) {
  // Hooks must run unconditionally — compute the merged components first,
  // then the empty-content early-return after.
  const merged = React.useMemo<Components>(
    () => ({ ...defaultComponents, ...(components ?? {}) }),
    [components],
  );

  // `String.trim()` only strips edges, so "  \n\t  " survives as "\n\t".
  // Test against whitespace-only with a regex so any all-whitespace input
  // (including tabs / interior newlines) returns null cleanly. The empty
  // path is the same whether streaming is true or not — consumers render
  // a `<TypingIndicator>` for the pre-first-token state.
  // Ahead of the whitespace guard on purpose — see `loading`.
  if (loading) {
    return (
      <div
        ref={ref}
        className={cn(PROSE_CLASSES, className)}
        role="status"
        aria-busy="true"
        aria-label="Loading response"
      >
        <SkeletonParagraph
          aria-hidden="true"
          lines={Math.max(1, loadingLines)}
          // Prose line-height, not the Skeleton default: the placeholder has to
          // occupy the same vertical space the paragraph will, or the row
          // resizes the moment the first token lands.
          className="[&>*]:h-3.5 [&>*]:rounded-md"
        />
      </div>
    );
  }

  if (!content || /^\s*$/.test(content)) return null;

  return (
    <div ref={ref} className={cn(PROSE_CLASSES, className)}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={merged}
      >
        {content}
      </ReactMarkdown>
      {streaming ? <StreamingCursor /> : null}
    </div>
  );
});
MarkdownInner.displayName = "Markdown";

/* ============================================================================
 * Streaming cursor
 *
 * Inline-block blinking caret rendered as the last child of the prose
 * container while live tokens are arriving. The shape (2px wide, 1.1em tall,
 * `bg-primary`) is mined from v3's chat-cursor span — same visual rhythm
 * across surfaces. `motion-reduce:animate-none` collapses the pulse to a
 * steady block under `prefers-reduced-motion`.
 *
 * Note on visual flow: the prose container's last block-level child (a `<p>`,
 * `<ul>`, fenced `<pre>`, etc.) ends with a block-end, so this caret renders
 * on the line below that block. That matches Claude.ai's streaming chrome
 * (cursor on its own line at the very end). If a future requirement needs
 * the caret inline with the last text node, it's a CSS `:has(:last-child)`
 * follow-up — out of scope here.
 *
 * `aria-hidden` because screen-reader users get the streaming cue from the
 * consumer's `aria-live` region (`<MessageRow role="assistant">` plus the
 * stream's own `role="status"`); rendering an extra "blinking caret" hint
 * would be SR noise.
 * ========================================================================= */

function StreamingCursor() {
  return (
    <span
      aria-hidden
      data-streaming-cursor
      className="inline-block w-0.5 h-[1.1em] ml-0.5 align-middle bg-primary animate-pulse motion-reduce:animate-none"
    />
  );
}

/**
 * `React.memo` wraps the forwardRef component so re-renders during streaming
 * chat skip when only sibling state changed. The default shallow prop check
 * is correct for our props (`content` is a string, `className` a string,
 * `components` should be a stable reference from the consumer — pass via
 * `useMemo` on the consumer side if it's built inline).
 */
export const Markdown = React.memo(MarkdownInner);
